import React, { useContext, useEffect, useRef, useState } from 'react';

import { Box, FormControl, InputLabel, MenuItem, Typography } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import { AppA } from '@vyce/core/src/components';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { AppKeyboardDatePicker, AppSelect, AppTextField } from '@vyce/core/src/components/inputs';
import { uploadFileRequest } from '@vyce/core/src/api/storage';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { DRCIR35Payload, DRCProfile, IR35_STATUSES } from '@vyce/core/src/types';
import {
  CEST_DOCUMENT_URL_MAX_LENGTH,
  CEST_GUIDANCE_URL,
  CEST_UPLOAD_HINT,
  IR35_OPTIONS,
} from '@vyce/core/src/constants';
import { formatDate } from '@vyce/core/src/utils/dates';

import { useStyles } from '../styles';

interface Props {
  profile: DRCProfile | null;
  saving: boolean;
  onSave: (payload: DRCIR35Payload) => Promise<boolean>;
}

const inputProps = { readOnly: true };

/**
 * CEST / employment-status outcome. Shown beside the DRC controls and deliberately gates nothing:
 * IR35 is not an input to whether the reverse charge applies.
 */
export const IR35Section: React.FC<Props> = ({ profile, saving, onSave }) => {
  const classes = useStyles();
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const [status, setStatus] = useState<IR35_STATUSES>(IR35_STATUSES.NOT_ASSESSED);
  const [determinedAt, setDeterminedAt] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');

  useEffect(() => {
    setStatus(profile?.ir35_status ?? IR35_STATUSES.NOT_ASSESSED);
    setDeterminedAt(profile?.ir35_determined_at || null);
    setReference(profile?.cest_reference || '');
    setDocumentUrl(profile?.cest_document_url || '');
  }, [profile]);

  const changed =
    status !== (profile?.ir35_status ?? IR35_STATUSES.NOT_ASSESSED) ||
    determinedAt !== (profile?.ir35_determined_at || null) ||
    reference.trim() !== (profile?.cest_reference || '') ||
    documentUrl.trim() !== (profile?.cest_document_url || '');

  // The gateway stores the link as given without checking that the file exists, and rejects
  // anything that is not https or is over 512 characters with a 422.
  const trimmedUrl = documentUrl.trim();
  const urlInvalid =
    !!trimmedUrl && (!trimmedUrl.startsWith('https://') || trimmedUrl.length > CEST_DOCUMENT_URL_MAX_LENGTH);

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset first, so picking the same file twice still fires a change event.
    event.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      const result = await uploadFileRequest(file);
      const url = result?.data?.url as string | undefined;
      if (!url) {
        showNotification({ message: 'Upload did not return a link', options: { variant: 'error' } });
        return;
      }
      setDocumentUrl(url);
    } catch (e) {
      handleServerError(e);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    const payload: DRCIR35Payload = { status };
    if (determinedAt !== (profile?.ir35_determined_at || null)) payload.determined_at = determinedAt;
    if (reference.trim() !== (profile?.cest_reference || ''))
      payload.cest_reference = reference.trim() || null;
    if (trimmedUrl !== (profile?.cest_document_url || '')) payload.cest_document_url = trimmedUrl || null;
    onSave(payload);
  };

  return (
    <Box>
      <Typography className={classes.sectionLabel}>CEST / employment status</Typography>
      <Typography className={classes.hint} style={{ marginTop: 4 }}>
        Run the check on <AppA href={CEST_GUIDANCE_URL} content="gov.uk" /> then record the outcome here.
        This does not affect whether the reverse charge applies.
      </Typography>

      <Box marginTop={1} display="flex" gridGap={8} flexWrap="wrap">
        <Box width={220}>
          {/* AppSelect reserves the top padding for a floating label — without one the value
              sits low and the control reads differently to the fields beside it. */}
          <FormControl fullWidth variant="filled">
            <InputLabel id="ir35-status">Employment status</InputLabel>
            <AppSelect
              labelId="ir35-status"
              fullWidth
              value={status}
              onChange={e => setStatus(e.target.value as IR35_STATUSES)}>
              {IR35_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </AppSelect>
          </FormControl>
        </Box>

        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Box width={200}>
            <AppKeyboardDatePicker
              label="Determined on"
              value={determinedAt || null}
              onChange={date => setDeterminedAt(date ? formatDate(date) : null)}
              format="dd/MM/yyyy"
              margin="none"
              fullWidth
              autoOk
              disableFuture
              InputProps={inputProps}
            />
          </Box>
        </MuiPickersUtilsProvider>

        <Box flex={1} minWidth={200}>
          <AppTextField
            label="CEST reference"
            value={reference}
            fullWidth
            onChange={e => setReference(e.target.value)}
          />
        </Box>
      </Box>

      <Box marginTop={1} display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          hidden
          onChange={handleFileSelected}
        />
        <AppButton
          variant="outlined"
          color="primary"
          size="small"
          loading={uploading}
          onClick={() => fileInputRef.current?.click()}>
          {documentUrl ? 'Replace CEST result' : 'Upload CEST result'}
        </AppButton>
        {!!trimmedUrl && !urlInvalid && <AppA href={trimmedUrl} content="View uploaded result" />}
      </Box>

      {urlInvalid && (
        <Typography variant="body2" color="error">
          {`The document link must start with https:// and be at most ${CEST_DOCUMENT_URL_MAX_LENGTH} characters.`}
        </Typography>
      )}

      <Typography className={classes.hint} style={{ marginTop: 6 }}>
        {CEST_UPLOAD_HINT}
      </Typography>

      <Box marginTop={2}>
        <AppButton
          variant="contained"
          color="primary"
          size="small"
          loading={saving}
          disabled={!changed || urlInvalid}
          onClick={handleSave}>
          Save employment status
        </AppButton>
      </Box>
    </Box>
  );
};
